import { CertificateType, EduType } from './types';

/**
 * 教育类型枚举
 * 使用枚举可以提高代码可读性和类型安全性
 */
export enum EducationType {
  /** 全日制教育 */
  FULL_TIME = '71',
  /** 非全日制教育 */
  PART_TIME = '78',
}

/**
 * 教育类型字典索引映射
 */
export const EducationTypeDictIndexMap = {
  '71': '全日制',
  '78': '非全日制',
};

/**
 * 附件类型映射
 */
export const AttachmentFileTypeMap = {
  // 最高全日制

  // -----毕业证明-----begin-----
  // 毕业证明，未勾选无法上传
  graduateProve: 15, // 使用的旧枚举值

  // 毕业证明，勾选无法上传，选择【已经取得毕业证书，但是毕业证丢失/损毁等】 reasonType=2
  // 且学历=【"高中（中专）及以下" 】 或【 学历≠"高中（中专）及以下" 且 对应的教育经历终止时间早于2001年】
  // 学校出具的已盖章的毕业证明
  stampGraduateProve: 101,

  // 毕业证明勾选无法上传，选择【已经取得毕业证书，但是毕业证丢失/损毁等】reasonType=2 前提下， 除了 101 的其他情况
  // 教育部学历证书电子注册备案表
  electronicRegister: 102,
  // -----毕业证明-----end-----

  // -----学位证明-----begin-----
  // 学位证明，未勾选无法上传
  degreeProve: 20, // 使用的旧枚举值

  // 学位证明勾选无法上传，选择【已经取得学位证书，但是学位证书丢失/损毁等】reasonType=5
  // 中国高等教育在线认证报告
  certificationReport: 103,
  // -----学位证明-----end-----

  // -----职业资格证书-----begin-----
  // 职业资格证书附件(选填)
  // 专科有，高中没有，所以是选填，这里高中和专科一起判断，没问题
  professionSong: 104,
  // -----职业资格证书-----end-----

  // -----留服认证书-----begin-----
  // 留服认证书，未勾选无法上传
  // 教育部留学服务中心学历学位认证书
  degreeCertification: 105,
  // 留服认证书，勾选无法上传，原因选择【教育部留学服务中心学历学位认证书申请中】reasonType=8
  // 教育部留学服务中心学历学位认证书申请截图
  degreeScreenshot: 106,
  // -----留服认证书-----end-----

  // -----学籍在线验证报告（中国实习生）-----begin-----
  // 教育部学籍在线验证报告，未勾选无法上传
  studentReport: 107,
  // 学校开具的在读证明，勾选无法上传【学校开具的在读证明】reasonType=9
  schoolStudyingProve: 108,
  // 学校官网的学籍截图，勾选无法上传【学校官网的学籍截图】reasonType=10
  studentStatusScreenshot: 109,
  // -----学籍在线验证报告-----end-----

  // -----在读证明（非中国实习生）-----begin-----
  // 在读证明
  studyingProve: 110,
  // -----在读证明-----end-----

  // 非全日制下所有的选择条件，跟全日制一摸一样
  // 毕业证明-非全
  noFullGraduateProve: 111,
  // 学校出具的已盖章的毕业证明-非全 reasonType=2
  noFullStampGraduateProve: 113,
  // 教育部学历证书电子注册备案表-非全 reasonType=2
  noFullElectronicRegister: 114,

  // 学位证明-非全
  noFullDegreeProve: 112,
  // 中国高等教育在线认证报告-非全 reasonType=5
  noFullCertificationReport: 115,

  // 职业资格证书附件(选填)-非全
  noFullProfessionSong: 116,

  // 留服认证书-非全
  // 教育部留学服务中心学历学位认证书-非全
  noFullDegreeCertification: 117,
  // 教育部留学服务中心学历学位认证书申请截图-非全 reasonType=8
  noFullDegreeScreenshot: 118,

  // 学籍在线验证报告（中国实习生）-非全
  // 教育部学籍在线验证报告-非全
  noFullStudentReport: 119,
  // 学校开具的在读证明-非全 reasonType=9
  noFullSchoolStudyingProve: 120,
  // 学校官网的学籍截图-非全 reasonType=10
  noFullStudentStatusScreenshot: 121,

  // 在读证明-非全
  noFullStudyingProve: 122,

  // 补充材料
  // 教育补充文件一
  eduSupplementFileOne: 123,
  // 教育补充文件二
  eduSupplementFileTwo: 124,
  // 教育补充文件三
  eduSupplementFileThree: 125,
  // 教育补充文件四
  eduSupplementFileFour: 126,
  // 教育补充文件五
  eduSupplementFileFive: 127,
  // 教育补充文件六
  eduSupplementFileSix: 128,
};

/**
 * 教育补充文件列表
 */
export const EduSupplementFileList = [
  AttachmentFileTypeMap.eduSupplementFileOne,
  AttachmentFileTypeMap.eduSupplementFileTwo,
  AttachmentFileTypeMap.eduSupplementFileThree,
  AttachmentFileTypeMap.eduSupplementFileFour,
  AttachmentFileTypeMap.eduSupplementFileFive,
  AttachmentFileTypeMap.eduSupplementFileSix,
];

/** 教育补充文件最大数量 */
export const MAX_SUPPLEMENT_FILE_NUM = 6;

/**
 * 教育类型对应的证书类型和原因类型配置
 */
export const EduTypeCertificateConfig = {
  [EduType.InternChinese]: {
    first: { certificateType: CertificateType.OnlineVerification },
    second: null,
    third: null,
  },
  [EduType.InternForeign]: {
    first: { certificateType: CertificateType.StudyProof },
    second: null,
    third: null,
  },
  [EduType.ChineseCollege]: {
    first: { certificateType: CertificateType.Diploma },
    second: { certificateType: CertificateType.Professional },
    third: null,
  },
  [EduType.ChineseNonCollege]: {
    first: { certificateType: CertificateType.Diploma },
    second: { certificateType: CertificateType.Degree },
    third: null,
  },
  [EduType.ForeignCollege]: {
    first: { certificateType: CertificateType.Diploma },
    second: { certificateType: CertificateType.ForeignAuth },
    third: null,
  },
  [EduType.ForeignNonCollege]: {
    first: { certificateType: CertificateType.Diploma },
    second: { certificateType: CertificateType.Degree },
    third: { certificateType: CertificateType.ForeignAuth },
  },
};

/**
 * 教育类型对应的证书类型和原因类型配置
 */
export const EduTypeCertificateConfigArray = {
  [EduType.InternChinese]: {
    first: { certificateType: CertificateType.OnlineVerification },
    second: { certificateType: null },
    third: { certificateType: null },
  },
  [EduType.InternForeign]: {
    first: { certificateType: CertificateType.StudyProof },
    second: { certificateType: null },
    third: { certificateType: null },
  },
  [EduType.ChineseCollege]: {
    first: { certificateType: CertificateType.Diploma },
    second: { certificateType: CertificateType.Professional },
    third: { certificateType: null },
  },
  [EduType.ChineseNonCollege]: {
    first: { certificateType: CertificateType.Diploma },
    second: { certificateType: CertificateType.Degree },
    third: { certificateType: null },
  },
  [EduType.ForeignCollege]: {
    first: { certificateType: CertificateType.Diploma },
    second: { certificateType: CertificateType.ForeignAuth },
    third: { certificateType: null },
  },
  [EduType.ForeignNonCollege]: {
    first: { certificateType: CertificateType.Diploma },
    second: { certificateType: CertificateType.Degree },
    third: { certificateType: CertificateType.ForeignAuth },
  },
};

/**
 * 需要展示无法上传原因类型
 */
export const showUnableUploadReasonIndex = [
  // reasonType：1，毕业证情况下：
  // 1、已经取得毕业证书，但是毕业证丢失/损毁等：reasonIndex：2
  2,

  // reasonType：2，学位证书情况下：
  // 1、已经取得学位证书，但是学位证书丢失/损毁等：reasonIndex：5
  5,

  // reasonType：3，留服认证情况下：
  // 1、教育部留学服务中心学历学位认证书申请中：reasonIndex：8
  8,

  // reasonType：4，学籍在线验证报告情况下：
  // 1、在读证明丢失：reasonIndex：9
  9,
  // 2、学校官网的学籍截图：reasonIndex：10
  10,
];

export const urlTypeMap = {
  image: 1,
  link: 2,
  text: 3,
};

/**
 * 按证书类型聚合的附件类型列表
 */
export const CertificateTypeAttachmentMap = {
  // 毕业证明
  [CertificateType.Diploma]: {
    fullTime: [
      AttachmentFileTypeMap.graduateProve,
      AttachmentFileTypeMap.stampGraduateProve,
      AttachmentFileTypeMap.electronicRegister,
    ],
    partTime: [
      AttachmentFileTypeMap.noFullGraduateProve,
      AttachmentFileTypeMap.noFullStampGraduateProve,
      AttachmentFileTypeMap.noFullElectronicRegister,
    ],
    unableUploadFullTime: [
      // 101
      AttachmentFileTypeMap.stampGraduateProve,
      // 113
      AttachmentFileTypeMap.noFullStampGraduateProve,
    ],
    unableUploadPartTime: [
      // 102
      AttachmentFileTypeMap.electronicRegister,
      // 114
      AttachmentFileTypeMap.noFullElectronicRegister,
    ],
    unableUploadAll() {
      return [...this.unableUploadFullTime, ...this.unableUploadPartTime];
    },
    all() {
      return [...this.fullTime, ...this.partTime];
    },
  },

  // 学位证明
  [CertificateType.Degree]: {
    fullTime: [AttachmentFileTypeMap.degreeProve, AttachmentFileTypeMap.certificationReport],
    partTime: [AttachmentFileTypeMap.noFullDegreeProve, AttachmentFileTypeMap.noFullCertificationReport],
    all() {
      return [...this.fullTime, ...this.partTime];
    },
  },

  // 职业资格证书
  [CertificateType.Professional]: {
    fullTime: [AttachmentFileTypeMap.professionSong],
    partTime: [AttachmentFileTypeMap.noFullProfessionSong],
    all() {
      return [...this.fullTime, ...this.partTime];
    },
  },

  // 留服认证书
  [CertificateType.ForeignAuth]: {
    fullTime: [AttachmentFileTypeMap.degreeCertification, AttachmentFileTypeMap.degreeScreenshot],
    partTime: [AttachmentFileTypeMap.noFullDegreeCertification, AttachmentFileTypeMap.noFullDegreeScreenshot],
    all() {
      return [...this.fullTime, ...this.partTime];
    },
  },

  // 学籍在线验证报告（中国实习生）
  [CertificateType.OnlineVerification]: {
    fullTime: [
      AttachmentFileTypeMap.studentReport,
      AttachmentFileTypeMap.schoolStudyingProve,
      AttachmentFileTypeMap.studentStatusScreenshot,
    ],
    partTime: [
      AttachmentFileTypeMap.noFullStudentReport,
      AttachmentFileTypeMap.noFullSchoolStudyingProve,
      AttachmentFileTypeMap.noFullStudentStatusScreenshot,
    ],
    all() {
      return [...this.fullTime, ...this.partTime];
    },
  },

  // 在读证明（非中国实习生）
  [CertificateType.StudyProof]: {
    fullTime: [AttachmentFileTypeMap.studyingProve],
    partTime: [AttachmentFileTypeMap.noFullStudyingProve],
    all() {
      return [...this.fullTime, ...this.partTime];
    },
  },
};

/** 默认附件类型 */
export const DEFAULT_ATTACHMENT_TYPE = {
  // 毕业证明
  [CertificateType.Diploma]: {
    fullTime: CertificateTypeAttachmentMap[CertificateType.Diploma].fullTime[0],
    partTime: CertificateTypeAttachmentMap[CertificateType.Diploma].partTime[0],
  },
  // 学位证明
  [CertificateType.Degree]: {
    fullTime: CertificateTypeAttachmentMap[CertificateType.Degree].fullTime[0],
    partTime: CertificateTypeAttachmentMap[CertificateType.Degree].partTime[0],
  },
  // 职业资格证书
  [CertificateType.Professional]: {
    fullTime: CertificateTypeAttachmentMap[CertificateType.Professional].fullTime[0],
    partTime: CertificateTypeAttachmentMap[CertificateType.Professional].partTime[0],
  },
  // 留服认证书
  [CertificateType.ForeignAuth]: {
    fullTime: CertificateTypeAttachmentMap[CertificateType.ForeignAuth].fullTime[0],
    partTime: CertificateTypeAttachmentMap[CertificateType.ForeignAuth].partTime[0],
  },
  // 学籍在线验证报告（中国实习生）
  [CertificateType.OnlineVerification]: {
    fullTime: CertificateTypeAttachmentMap[CertificateType.OnlineVerification].fullTime[0],
    partTime: CertificateTypeAttachmentMap[CertificateType.OnlineVerification].partTime[0],
  },
  // 在读证明（非中国实习生）
  [CertificateType.StudyProof]: {
    fullTime: CertificateTypeAttachmentMap[CertificateType.StudyProof].fullTime[0],
    partTime: CertificateTypeAttachmentMap[CertificateType.StudyProof].partTime[0],
  },
};

/** 博士学历提示文案 */
export const DOCTOR_TIP =
  '请确保博士和本科（大专）阶段的教育经历完整填写，如硕士研究生阶段单独颁发毕业证/学位证，可点击+号添加硕士研究生阶段的教育经历';
